import { useCallback, useMemo, useState } from 'react';
import { RotateCw, X } from 'lucide-react';
import { pipelineApiFetch, readJsonResponse } from '../../api';
import { ATLAS_LABEL_COLOR_MAP, DEFAULT_COLOR_MAP } from '../constants';
import { applyAtlasTextIntensityToViewer, parseTextIntensityValues } from '../lib/atlasColoring';
import { isPatientOrClinicalPointName } from '../lib/pointNames';
import { FilterDropdown } from './FilterDropdown';


// -------------------------------
// Subcomponent: SettingsPanel
// Contains upload controls and visualization settings
// -------------------------------
export const SettingsPanel = ({
    viewer,
    selectedFormat = 'freesurferasc',
    setSelectedFormat = () => {},
    selectedBackground,
    setSelectedBackground,
    setHasIntensityData,
    setCurrentObjectFilename,
    onClose,
    settingsState,
    setSettingsState,
    brainOpacity,
    wireframeEnabled,
    setWireframeEnabled,
    applyOpacityToScene,
    applyWireframeState,
    selectedCenters = [],
    selectedPatients = [],
    onCenterToggle,
    onPatientToggle,
    centers = [],
    patients = [],
    centersLoading = false,
    centersError = null,
    patientsLoading = false,
    patientsError = null
}) => {
    const [isSyncing, setIsSyncing] = useState(false);
    const [syncMessage, setSyncMessage] = useState('');

    // Helper function to update settings state
    const updateSettingsState = useCallback((updates) => {
        setSettingsState(prev => ({ ...prev, ...updates }));
    }, [setSettingsState]);

    // Break the selected patients down by site (center), with a count per site.
    const selectedPatientSites = useMemo(() => {
        if (!selectedPatients.length) return [];
        const counts = new Map();
        selectedPatients.forEach((patientId) => {
            const center = centers.find((c) => c.patients?.includes(patientId));
            const name = center ? center.label : 'Unknown site';
            counts.set(name, (counts.get(name) || 0) + 1);
        });
        return [...counts.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    }, [selectedPatients, centers]);

    // Destructure state for easier access
    const {
        selectedColorMap,
        selectedHemisphere,
        giiEnabled
    } = settingsState;

    const handleChangeColorMap = (event) => {
        const url = event.target.value;
        // Selecting a color map turns on the Intensity Data toggle and applies the
        // chosen map to the atlas intensity for the current hemisphere.
        updateSettingsState({ selectedColorMap: url, giiEnabled: true });

        if (viewer && url) {
            loadGiiFile(selectedHemisphere, url);
        }
    };

    const handleSyncData = useCallback(async () => {
        if (isSyncing) return;

        setIsSyncing(true);
        setSyncMessage('Comparing accessible Pennsieve datasets with PostgreSQL...');
        try {
            const response = await pipelineApiFetch('/api/v1/pipeline/sync', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ dry_run: false }),
                timeoutMs: 120000
            });
            const payload = await readJsonResponse(response, 'Could not sync Pennsieve datasets');
            if (!response.ok) {
                throw new Error(payload.message || payload.error || 'Could not sync Pennsieve datasets');
            }
            setSyncMessage(
                `Found ${payload.accessible_count || 0} accessible; ` +
                `${payload.existing_count || 0} already present; ` +
                `${payload.triggered_count || 0} newly queued in AWS.`
            );
        } catch (error) {
            setSyncMessage(error?.message || 'Cloud sync could not be started.');
        } finally {
            setIsSyncing(false);
        }
    }, [isSyncing]);

    // Load atlas intensity data based on hemisphere.
    const loadGiiFile = useCallback((hemisphere, colorMapOverride = null) => {
        if (!viewer) return;

        // All hemispheres use the per-hemisphere text atlas (vertex counts verified
        // to match each surface). Single-hemisphere GIFTI loading was both
        // mis-colored (wrong color map) and could leave the loading overlay stuck
        // when the left-hemisphere file failed, so the text path is used uniformly.
        const atlasIntensityFiles = {
            'lh': { url: '/atlas/atlas_lh.txt', format: 'text' },
            'rh': { url: '/atlas/atlas_rh.txt', format: 'text' },
            'lh_rh': { url: '/atlas/atlas.txt', format: 'text' }
        };

        const intensityFile = atlasIntensityFiles[hemisphere];
        if (!intensityFile) {
            console.error('Invalid hemisphere for atlas intensity file:', hemisphere);
            return;
        }

        const loadingElement = document.createElement('div');
        loadingElement.className = 'loading-overlay';
        // loadingElement.innerHTML = 'Loading intensity data...';
        const container = document.getElementById('brainbrowser');
        if (container) {
            container.appendChild(loadingElement);
        }

        const effectiveColorMap = colorMapOverride || selectedColorMap;
        const colorMapToUse = intensityFile.format === 'text' &&
            (!effectiveColorMap || effectiveColorMap === DEFAULT_COLOR_MAP)
            ? ATLAS_LABEL_COLOR_MAP
            : effectiveColorMap || DEFAULT_COLOR_MAP;

        if (intensityFile.format === 'text') {
            Promise.all([
                fetch(intensityFile.url).then(response => {
                    if (!response.ok) throw new Error(`Could not load atlas file (${response.status}).`);
                    return response.text();
                }),
                fetch(colorMapToUse).then(response => {
                    if (!response.ok) throw new Error(`Could not load atlas color map (${response.status}).`);
                    return response.text();
                })
            ]).then(([intensityText, colorMapText]) => {
                applyAtlasTextIntensityToViewer({
                    viewer,
                    values: parseTextIntensityValues(intensityText),
                    colorMapText
                });
                if (loadingElement.parentNode) loadingElement.remove();
                updateSettingsState({
                    intensityApplied: true,
                    selectedColorMap: colorMapToUse,
                    giiEnabled: true,
                    intensityFormatWarning: null
                });
                setHasIntensityData(true);
            }).catch((error) => {
                console.error('Error applying atlas text intensity file:', error);
                if (loadingElement.parentNode) loadingElement.remove();
                const message = error?.message || 'Could not apply atlas text colors.';
                updateSettingsState({ giiEnabled: false, intensityApplied: false, intensityFormatWarning: message });
                alert(`Error loading atlas intensity file: ${message}`);
            });
            return;
        }

        viewer.loadColorMapFromURL(colorMapToUse, {
            cursor_color: "#FFFFFF",
            complete: () => {
                viewer.loadIntensityDataFromURL(intensityFile.url, {
                    format: intensityFile.format,
                    blend: false,
                    complete: () => {
                        loadingElement.remove();
                        updateSettingsState({
                            intensityApplied: true,
                            selectedColorMap: colorMapToUse
                        });
                        setHasIntensityData(true);
                        viewer.render();
                    },
                    error: (error) => {
                        console.error('Error loading atlas intensity file:', error);
                        if (loadingElement.parentNode) loadingElement.remove();
                        const message = error?.message || error?.error_message || 'The file may not exist for this hemisphere.';
                        updateSettingsState({ giiEnabled: false, intensityApplied: false, intensityFormatWarning: message });
                        alert(`Error loading atlas intensity file: ${message}`);
                    }
                });
            },
            error: (error) => {
                console.error('Error loading atlas color map:', error);
                if (loadingElement.parentNode) loadingElement.remove();
                const message = error?.message || error?.error_message || 'Could not load the atlas color map.';
                updateSettingsState({ giiEnabled: false, intensityApplied: false, intensityFormatWarning: message });
                alert(`Error loading atlas color map: ${message}`);
            }
        });
    }, [viewer, selectedColorMap, updateSettingsState, setHasIntensityData]);

    // Load brain model based on hemisphere selection
    const loadBrainModel = useCallback((hemisphere, shouldLoadGii = null) => {
        if (!viewer) return;

        // Map hemisphere to file path
        const modelFiles = {
            'lh': '/atlas/lh.pial.asc',
            'rh': '/atlas/rh.pial.asc',
            'lh_rh': '/atlas/lh_rh.pial.asc'
        };

        const modelFile = modelFiles[hemisphere];
        if (!modelFile) {
            console.error('Invalid hemisphere selection:', hemisphere);
            return;
        }

        // Use provided parameter, or fall back to current state
        const loadGii = shouldLoadGii !== null ? shouldLoadGii : giiEnabled;

        const loadingElement = document.createElement('div');
        loadingElement.className = 'loading-overlay';
        // loadingElement.innerHTML = 'Loading brain model...';
        const container = document.getElementById('brainbrowser');
        if (container) {
            container.appendChild(loadingElement);
        }

        // Preserve points
        const pointsToPreserve = [];
        if (viewer.model) {
            viewer.model.traverse(object => {
                if (object && object.name) {
                    if (isPatientOrClinicalPointName(object.name) || object.name === 'uploaded-points') {
                        pointsToPreserve.push({
                            object: object,
                            visible: object.visible
                        });
                    }
                }
            });

            // Remove points temporarily
            pointsToPreserve.forEach(point => {
                viewer.model.remove(point.object);
            });

            // Clear all other children
            while (viewer.model.children.length > 0) {
                const child = viewer.model.children[0];
                viewer.model.remove(child);
            }
        }

        // Clear stale model_data before loading the new surface. BrainBrowser's
        // model_data.get() (no name) returns the FIRST loaded model, and the store
        // is only cleared by clearScreen() — which this custom swap doesn't call.
        // Without clearing, after switching to a single hemisphere the store still
        // reports the original whole-brain vertex count (271082), so the
        // single-hemisphere atlas (137427/133655) fails the length check.
        viewer.model_data?.clear?.();

        viewer.loadModelFromURL(modelFile, {
            format: 'freesurferasc',
            complete: () => {
                const targetOpacity = Math.max(0, Math.min(typeof brainOpacity === 'number' ? brainOpacity : 1, 1));

                // Restore points
                pointsToPreserve.forEach(point => {
                    viewer.model.add(point.object);
                    point.object.visible = point.visible;
                });

                // Set initial opacity for fade-in
                viewer.model.traverse(object => {
                    if (object.type === 'Mesh' &&
                        object.geometry &&
                        object.geometry.type !== 'SphereGeometry' &&
                        !object.name?.includes('-points') &&
                        object.name !== 'uploaded-points') {

                        if (object.material) {
                            object.material.transparent = true;
                            object.material.opacity = 0;

                            if (Array.isArray(object.material)) {
                                object.material.forEach(material => {
                                    material.transparent = true;
                                    material.opacity = 0;
                                });
                            }
                        }
                    }
                });

                viewer.render();
                if (loadingElement.parentNode) {
                    loadingElement.remove();
                }

                // Fade in animation
                const fadeDuration = 1000;
                const startTime = Date.now();
                const fadeIn = () => {
                    const elapsed = Date.now() - startTime;
                    const progress = Math.min(elapsed / fadeDuration, 1);
                    const easedProgress = 1 - Math.pow(1 - progress, 3);
                    const currentOpacity = easedProgress * targetOpacity;

                    viewer.model.traverse(object => {
                        if (object.type === 'Mesh' &&
                            object.geometry &&
                            object.geometry.type !== 'SphereGeometry' &&
                            !object.name?.includes('-points') &&
                            object.name !== 'uploaded-points') {

                            const applyOpacity = (material) => {
                                if (!material) {
                                    return;
                                }
                                material.opacity = currentOpacity;
                                material.transparent = true;
                            };

                            if (object.material) {
                                if (Array.isArray(object.material)) {
                                    object.material.forEach(applyOpacity);
                                } else {
                                    applyOpacity(object.material);
                                }
                            }
                        }
                    });

                    viewer.render();

                    if (progress < 1) {
                        requestAnimationFrame(fadeIn);
                    } else {
                        applyOpacityToScene?.(targetOpacity);
                        applyWireframeState?.(wireframeEnabled);
                        viewer.render();
                    }
                };

                requestAnimationFrame(fadeIn);

                // Update state
                const filename = hemisphere === 'lh_rh' ? 'lh_rh.pial.asc' : `${hemisphere}.pial.asc`;
                setCurrentObjectFilename(filename);
                updateSettingsState({ objectApplied: true });

                // If .gii should be loaded, load it after model is loaded
                if (loadGii) {
                    loadGiiFile(hemisphere);
                } else {
                    // Clear intensity data if .gii is disabled
                    setHasIntensityData(false);
                    updateSettingsState({ intensityApplied: false });
                }
            },
            error: (error) => {
                console.error('Error loading brain model:', error);
                if (loadingElement.parentNode) {
                    loadingElement.remove();
                }
                alert('Error loading brain model. Please try again.');
            }
        });
    }, [
        viewer,
        setCurrentObjectFilename,
        updateSettingsState,
        giiEnabled,
        setHasIntensityData,
        loadGiiFile,
        brainOpacity,
        wireframeEnabled,
        applyOpacityToScene,
        applyWireframeState
    ]);

    // Handle hemisphere change
    const handleHemisphereChange = useCallback((event) => {
        const newHemisphere = event.target.value;
        updateSettingsState({ selectedHemisphere: newHemisphere, intensityFormatWarning: null });
        loadBrainModel(newHemisphere);
    }, [updateSettingsState, loadBrainModel]);

    // Handle .gii toggle
    const handleGiiToggle = useCallback((event) => {
        const enabled = event.target.checked;
        updateSettingsState({ giiEnabled: enabled });

        if (enabled) {
            // Load .gii file
            loadGiiFile(selectedHemisphere);
        } else {
            // Clear intensity data by reloading the model without intensity data
            if (!viewer) return;
            setHasIntensityData(false);
            updateSettingsState({ intensityApplied: false });

            // Clear intensity data from model_data before reloading
            try {
                if (viewer.model_data) {
                    // Clear intensity_data for all models
                    if (viewer.model_data.forEach) {
                        viewer.model_data.forEach((modelData) => {
                            if (modelData && modelData.intensity_data) {
                                modelData.intensity_data.length = 0;
                            }
                        });
                    }
                }
            } catch (error) {
                console.error('Error clearing intensity data from model_data:', error);
            }

            // Reload the model without intensity data - pass false to explicitly not load .gii
            loadBrainModel(selectedHemisphere, false);
        }
    }, [updateSettingsState, selectedHemisphere, loadGiiFile, loadBrainModel, viewer, setHasIntensityData]);

    return (
        <div className="settings-panel">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '1px solid rgba(255, 255, 255, 0.2)', paddingBottom: '8px' }}>
                <h3 style={{ margin: 0 }}>Settings</h3>
                <button
                    onClick={onClose}
                    style={{
                        background: 'transparent',
                        border: 'none',
                        color: 'white',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                    title="Close settings"
                >
                    <X size={16} />
                </button>
            </div>
            <div className="settings-content">
                <section className="settings-section">
                    <h4>Brain Model</h4>
                    <div style={{ marginBottom: '12px' }}>
                        <select
                            value={selectedHemisphere}
                            onChange={handleHemisphereChange}
                            style={{
                                width: '100%',
                                padding: '8px',
                                borderRadius: '4px',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                background: 'rgba(0, 0, 0, 0.3)',
                                color: 'white',
                                fontSize: '14px'
                            }}
                        >
                            <option value="lh">Left Hemisphere (LH)</option>
                            <option value="rh">Right Hemisphere (RH)</option>
                            <option value="lh_rh">Both Hemispheres (LH + RH)</option>
                        </select>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '14px' }}>Intensity Data</span>
                        <div className="toggle-switch">
                            <input
                                type="checkbox"
                                id="gii-toggle"
                                className="toggle-checkbox"
                                checked={giiEnabled}
                                onChange={handleGiiToggle}
                            />
                            <label htmlFor="gii-toggle" className="toggle-label"></label>
                        </div>
                    </div>
                </section>

                {onCenterToggle && onPatientToggle && (
                    <section className="settings-section">
                        <h4>Plot Data Points</h4>
                        <div className="file-selectors two-column">
                            <FilterDropdown
                                label="Centers"
                                count={selectedCenters.length}
                                options={centers}
                                selectedItems={selectedCenters}
                                onToggle={onCenterToggle}
                                loading={centersLoading}
                                error={centersError}
                            />
                            <FilterDropdown
                                label="Patients"
                                count={selectedPatients.length}
                                options={patients}
                                selectedItems={selectedPatients}
                                onToggle={onPatientToggle}
                                loading={patientsLoading}
                                error={patientsError}
                            />
                        </div>
                        {selectedPatientSites.length > 0 && (
                            <div className="selected-patient-sites">
                                <span className="selected-patient-sites-label">Sites:</span>
                                {selectedPatientSites.map(([name, count]) => (
                                    <span key={name} className="selected-patient-site">{name} ({count})</span>
                                ))}
                            </div>
                        )}
                    </section>
                )}

                <section className="settings-section">
                    <h4>Color Map</h4>
                    <div className="color-map-selector">
                        <select
                            id="color-map-select-settings"
                            value={selectedColorMap || ''}
                            onChange={handleChangeColorMap}
                            className="color-map-dropdown"
                        >
                            <option value="/brainbrowser/examples/color-maps/spectral.txt">Spectral</option>
                            <option value="/brainbrowser/examples/color-maps/thermal.txt">Thermal</option>
                            <option value="/brainbrowser/examples/color-maps/gray-scale.txt">Gray</option>
                            <option value="/brainbrowser/examples/color-maps/blue.txt">Blue</option>
                            <option value="/brainbrowser/examples/color-maps/green.txt">Green</option>
                        </select>
                    </div>
                </section>

                <section className="settings-section">
                    <h4>Background Color</h4>
                    <div className="view-options">
                        <select
                            value={selectedBackground}
                            onChange={(e) => {
                                setSelectedBackground(e.target.value);
                                viewer?.setClearColor(parseInt(e.target.value, 16));
                            }}
                        >
                            {[
                                { label: 'Black', value: '0x000000' },
                                { label: 'Gray', value: '0x888888' },
                                { label: 'White', value: '0xffffff' },
                                { label: 'Red', value: '0xff0000' },
                                { label: 'Green', value: '0x00ff00' },
                                { label: 'Blue', value: '0x0000ff' }
                            ].map(color => (
                                <option key={color.value} value={color.value}>{color.label}</option>
                            ))}
                        </select>
                    </div>
                </section>

                <div className="settings-sync-row">
                    <button
                        className="sync-button"
                        onClick={handleSyncData}
                        disabled={isSyncing}
                        title="Discover Pennsieve datasets and ingest only datasets absent from PostgreSQL"
                    >
                        <RotateCw size={16} className={isSyncing ? 'spinning' : ''} />
                        <span>Sync Data</span>
                    </button>
                    {syncMessage && <p className="sync-status-message">{syncMessage}</p>}
                </div>
            </div>
        </div>
    );
};
